// Site metrics (tallerza.cl), aggregated per day by the `evento` Function in
// `metricas/{YYYY-MM-DD}` (Santiago calendar day) with counters in maps.
// Pure helpers for the superadmin's Métricas page: the date range, adding a
// range of days up and the labels.
import { CLAVE_OTROS, claveLegible, type Metricas } from '@taller/shared';
import { z } from 'zod';
import { diaIso, sumarDiasIso } from '@/lib/tiempo';

export type Contadores = Record<string, number>;

/** Every map is optional: a day only has the keys something happened on. */
export type MetricaDia = Partial<Metricas> & { id: string };

// ---- Range (typed search param) ----

export const RANGOS = [7, 30, 90] as const;
export type Rango = (typeof RANGOS)[number];
export const RANGO_POR_DEFECTO: Rango = 30;

export const busquedaMetricas = z.object({
  dias: z.coerce
    .number()
    .refine((n): n is Rango => (RANGOS as readonly number[]).includes(n))
    .optional()
    .catch(undefined),
});

/** The last `dias` Santiago days, today included, oldest first. */
export function diasDelRango(dias: number, ahora = Date.now()): string[] {
  const hoy = diaIso(ahora);
  return Array.from({ length: dias }, (_, i) => sumarDiasIso(hoy, i - (dias - 1)));
}

// ---- Aggregation ----

export type Fila = { clave: string; valor: number };

export type Resumen = {
  vistas: number;
  leads: number;
  /** leads / vistas, or null without views (never divide by zero). */
  conversion: number | null;
  whatsapp: number;
  demoAbierta: number;
  ingresar: number;
  porDia: { dia: string; vistas: number }[];
  paginas: Fila[];
  referentes: Fila[];
  fuentes: Fila[];
  medios: Fila[];
  campanas: Fila[];
  dispositivos: Fila[];
  /** How many days of the range have a document at all. */
  diasConDatos: number;
};

const numero = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

export const total = (m: Contadores | undefined) =>
  Object.values(m ?? {}).reduce((s, v) => s + numero(v), 0);

function sumarEn(destino: Map<string, number>, m: Contadores | undefined) {
  for (const [k, v] of Object.entries(m ?? {})) destino.set(k, (destino.get(k) ?? 0) + numero(v));
}

/** Biggest first; ties alphabetical so the order is stable. */
const ordenar = (m: Map<string, number>): Fila[] =>
  [...m]
    .filter(([, v]) => v > 0)
    .map(([clave, valor]) => ({ clave, valor }))
    .sort((a, b) => b.valor - a.valor || a.clave.localeCompare(b.clave));

/**
 * The day's views: `eventos.vista` counts every one; the per-page map adds
 * up to the same (overflow goes to `otros`), used if the former is missing.
 */
const vistasDelDia = (d: MetricaDia) =>
  typeof d.eventos?.vista === 'number' ? numero(d.eventos.vista) : total(d.vistas);

type Mapa = 'vistas' | 'referentes' | 'fuentes' | 'medios' | 'campanas' | 'dispositivos';

/**
 * Adds up the days of `dias` (docs outside it are ignored, days without a
 * doc count as zero so the chart shows the gaps).
 */
export function resumir(docs: readonly MetricaDia[], dias: readonly string[]): Resumen {
  const enRango = new Set(dias);
  const porId = new Map(docs.filter((d) => enRango.has(d.id)).map((d) => [d.id, d]));
  const mapas: Record<Mapa, Map<string, number>> = {
    vistas: new Map(),
    referentes: new Map(),
    fuentes: new Map(),
    medios: new Map(),
    campanas: new Map(),
    dispositivos: new Map(),
  };
  const eventos = new Map<string, number>();
  for (const d of porId.values()) {
    for (const k of Object.keys(mapas) as Mapa[]) sumarEn(mapas[k], d[k]);
    sumarEn(eventos, d.eventos);
  }
  const vistas = [...porId.values()].reduce((s, d) => s + vistasDelDia(d), 0);
  const leads = eventos.get('lead') ?? 0;
  return {
    vistas,
    leads,
    conversion: vistas > 0 ? leads / vistas : null,
    whatsapp: eventos.get('whatsapp') ?? 0,
    demoAbierta: eventos.get('demo_abierta') ?? 0,
    ingresar: eventos.get('ingresar') ?? 0,
    porDia: dias.map((dia) => {
      const d = porId.get(dia);
      return { dia, vistas: d ? vistasDelDia(d) : 0 };
    }),
    paginas: ordenar(mapas.vistas),
    referentes: ordenar(mapas.referentes),
    fuentes: ordenar(mapas.fuentes),
    medios: ordenar(mapas.medios),
    campanas: ordenar(mapas.campanas),
    dispositivos: ordenar(mapas.dispositivos),
    diasConDatos: porId.size,
  };
}

// ---- Labels ----

const fmtEntero = new Intl.NumberFormat('es-CL');
const fmtPorcentaje = new Intl.NumberFormat('es-CL', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export const entero = (n: number) => fmtEntero.format(n);
export const porcentaje = (n: number | null) => (n === null ? '—' : fmtPorcentaje.format(n));

export { CLAVE_OTROS };

const ETIQUETAS_DISPOSITIVO: Record<string, string> = {
  movil: 'Celular',
  escritorio: 'Computador',
};

export const etiquetaDispositivo = (k: string) =>
  ETIQUETAS_DISPOSITIVO[k] ?? (k === CLAVE_OTROS ? 'Otros' : k);

/** Page keys are paths with '/' stored as '_' ("_" is the home page). */
export const etiquetaPagina = (k: string) =>
  k === CLAVE_OTROS ? 'Otras páginas' : claveLegible('vistas', k);

/** Referrer keys are hosts with '.' stored as '_'. */
export const etiquetaReferente = (k: string) =>
  k === CLAVE_OTROS ? 'Otros sitios' : claveLegible('referentes', k);

/** Campaign values are kept as sent (lowercased); overflow reads "Otros". */
export const etiquetaCampana = (k: string) => (k === CLAVE_OTROS ? 'Otros' : k);
